import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from 'react-i18next';
import colors from '../theme/colors';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.background.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 13000;
  padding: 20px;
`;

const Content = styled(motion.div)`
  background: ${colors.base.themePrimaryTransparent};
  border-radius: 16px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  box-shadow: ${colors.effects.shadowXLarge};
  text-align: center;
  border: 1px solid ${colors.border.light};

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Title = styled.h2`
  font-size: 1.6rem;
  color: ${colors.accent.primary};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 700;
  text-shadow: ${colors.effects.textShadowGlow};

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Text = styled.p`
  font-size: 0.9rem;
  color: ${colors.text.light};
  line-height: 1.6;
  margin: 0 0 24px 0;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
`;

const CloseButton = styled.button`
  background: ${colors.background.white10};
  color: ${colors.text.light};
  border: 1px solid ${colors.border.light};
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: ${colors.background.white20};
  }
`;

const PaymentSuccessModal = ({ isOpen, onClose, title, message, closeLabel, isAuthenticated = true }) => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const [dbTitle, setDbTitle] = useState(null);
  const [dbMessage, setDbMessage] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/about/content');
        const data = await res.json();
        if (!cancelled && data && data.success && Array.isArray(data.content)) {
          const byKey = Object.fromEntries(data.content.map((s) => [s.section_key, s]));
          const t = byKey['payment_success_title'];
          const m = byKey['payment_success_message'];
          const lang = currentLanguage === 'fr' ? 'fr' : 'en';
          if (t) setDbTitle(t[`title_${lang}`] || t.title_en || t.title_fr || null);
          if (m) {
            const raw = m[`content_${lang}`] || m.content_en || m.content_fr || '';
            setDbMessage(raw);
          }
        }
      } catch (_) {
        // Fallback handled by defaults below
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, currentLanguage]);

  // Close thank-you modal with Escape key
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  const defaultTitle = currentLanguage === 'fr'
    ? 'Merci beaucoup pour ton paiement !'
    : 'Thank you so much for your payment!';

  // Different message for authenticated vs non-authenticated users
  const defaultMsgAuthenticated = currentLanguage === 'fr'
    ? "Ton adhésion Premium sera activée en moins d'une minute. Si ce n'est pas immédiat, ferme cette fenêtre et réouvres le menu Premium: nous vérifierons automatiquement ton statut et mettrons à jour ta session."
    : 'Your Premium membership will be activated in less than a minute. If it is not immediate, close this window and reopen the Premium menu: we will automatically check your status and update your session.';

  const defaultMsgNotAuthenticated = currentLanguage === 'fr'
    ? "Ta contribution est très appréciée et nous aide à aider plus de gens à mieux dormir. Si tu souhaites accéder aux fonctionnalités Premium, connecte-toi avec Google et nous activerons ton compte sous 24 heures."
    : 'Your contribution is greatly appreciated and helps us help more people sleep better. If you would like to access Premium features, please sign in with Google and we will activate your account within 24 hours.';

  const defaultMsg = isAuthenticated ? defaultMsgAuthenticated : defaultMsgNotAuthenticated;

  const i18nTitle = t('paymentSuccess.title', { defaultValue: '' });
  const i18nMessage = isAuthenticated
    ? t('paymentSuccess.message', { defaultValue: '' })
    : t('paymentSuccess.messageNotAuthenticated', { defaultValue: '' });

  const finalTitle = title || (i18nTitle || '').trim() || dbTitle || defaultTitle;
  const finalMessage = message || (i18nMessage || '').trim() || (isAuthenticated ? dbMessage : null) || defaultMsg;

  return createPortal(
    (
      <AnimatePresence>
        {isOpen && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            <Content
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Title>
                <span>🎉</span>
                {finalTitle}
                <span>🎉</span>
              </Title>
              <Text>
                {finalMessage}
              </Text>
              <ButtonRow>
                <CloseButton onClick={onClose}>
                  {closeLabel || t('common.close') || 'Close'}
                </CloseButton>
              </ButtonRow>
            </Content>
          </Overlay>
        )}
      </AnimatePresence>
    ),
    document.body
  );
};

export default PaymentSuccessModal;

